import { CommonModule } from '@angular/common';
import { ErrorHandler, inject, NgModule, provideAppInitializer, CSP_NONCE } from '@angular/core';
import { LoaderModule } from '@monsido/core/loader/loader.module';
import { LoadingModule } from '@monsido/services/loading/loading.module';
import { MonEventModule } from '@monsido/services/mon-event/mon-event.module';
import { MonBiModule } from '@client/app/modules/business-intelligence/bi.module';
import { MonScoreComponentsModule } from './modules/score/components/mon-score-components.module';
import { MonScoreServicesModule } from './modules/score/services/mon-score-services.module';
import { MonBrowserModule } from '@monsido/services/mon-browser/mon-browser.module';
import { MonLegacyBrowserModule } from '@monsido/modules/legacy-browsers/legacy-browser.module';
import { MonCookieBannerModule } from '@monsido/modules/cookie-banner/cookie-banner.module';
import { RollbarService, RollbarErrorHandlerService, rollbarFactory } from '../ng2/external/rollbar/rollbar-error-handler.service';
import { OverlayService } from '@monsido/services/overlay-service/overlay.service';
import { AccessibilityComponentsModule } from './modules/accessibility/components/accessibility-components.module';
import { MonIssueModule } from '@monsido/modules/issue/issue.module';
import { QaModule } from './modules/qa/qa.module';
import { PageDetailsModule } from '@monsido/modules/page-details/page-details.module';
import { DataPrivacyModule } from '@monsido/modules/data-privacy/data-privacy.module';
import { MonLabelsModule } from '@monsido/modules/labels/labels.module';
import { ActiveFeatureModule } from '@monsido/services/active-feature/active-feature.module';
import { OverlayWrapperModule } from './modules/overlay-wrapper/overlay-wrapper.module';
import { DirectNavigationService } from '@monsido/services/direct-navigation-service/direct-navigation.service';
import { CustomerHasPlanPipe } from '@monsido/ng2/shared/pipes/customer-has-plan/customer-has-plan.pipe';
import { StatisticsModule } from '@monsido/modules/statistics/statistics.module';
import { IsStatsDemoDataPipe } from '@client/app/shared-modules/pipes/is-stats-demo-data.pipe';
import { ReportCenterModule } from '@monsido/modules/report-center/report-center.module';
import { RolePipe } from '@monsido/core/pipes/role/role.pipe';
import { PipeService } from './filters/pipe-transformation.service';
import { MonInventoryModule } from '@monsido/ng2/modules/inventory/inventory.module';
import { UIRouterModule } from '@uirouter/angular';
import {
    NgbDateAdapter,
    NgbDateNativeAdapter,
    NgbModule, NgbTimeAdapter,
    NgbTimeStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { TrackingModule } from '@client/app/modules/tracking/tracking.module';
import { TranslateModule } from '@client/app/modules/translate.module';
import { ExportModule } from '@client/app/modules/export/export.module';
import { AdminModule } from '@client/app/feature-modules/admin/admin.module';
import { NotificationsStates } from '@client/app/feature-modules/navigation/notifications/states/notifications.state';
import { providePrimeNG } from 'primeng/config';
import { OptimizePreset } from '@client/theme';
import { PagePerformanceModule } from '@client/app/modules/page-performance/page-performance.module';
import { QuickHelpComplianceStatusModule } from '@client/app/components/quick-help-compliance-status/quick-help-compliance-status.module';
import { TermsModule } from '@client/app/modules/terms/terms.module';
import { CacheModule } from './modules/cache/cache.module';
import { ChatModule } from './modules/chat/chat.module';
import { SpotlightModule } from './modules/spotlight/spotlight.module';
import { MomentsModule } from './modules/moments/moments.module';
import { DeeplinkModule } from './modules/deeplink/deeplink.module';
import { ModalsModule } from './modules/modals/modals.module';
import { TranslocoRootModule } from './transloco-root.module';
import { PrimeNGTranslationService } from './services/primeng-translation.service';
import { ExtensionModule } from './modules/extension/extension.module';
import { NotificationsModule } from './modules/notifications/notifications.module';
import { ChartsModule } from './modules/charts/charts.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UpgradePipeModule } from '@client/app/filters/upgrade-pipe.module';
import { PagesModule } from '@client/app/pages/pages.module';
import {
    DialogUpgradeWrapperComponent,
} from '@client/app/modules/dialog-upgrade-wrapper/dialog-upgrade-wrapper.component';
import { ComponentsModule } from './components/components.module';
import { UpgradeFormsModule } from '@client/app/forms/forms.module';
import { DomainFormModule } from '@client/app/forms/domain/domain-form.module';
import { UpgradeDataPrivacyModule } from '@client/app/modules/data-privacy/data-privacy.module';
import { UpgradeGlobalModule } from '@client/app/pages/global/global.module';
import { UpgradePolicyModule } from '@client/app/pages/policy/policy.module';
import { UpgradeDomainModule } from '@client/app/pages/domain/domain.module';
import { UpgradeDashboardModule } from '@client/app/pages/dashboard/dashboard.module';
import { UpgradeUptimesModule } from '@client/app/pages/uptimes/uptimes.module';
import { UpgradeHeatmapsModule } from '@client/app/modules/heatmaps/heatmaps.module';
import { UpgradeLayoutModule } from '@client/app/layout/layout.module';
import { UIRouter } from '@uirouter/core';
import { LAYOUT_STATES } from '@client/app/layout/layout.route';
import { AppComponent } from '@client/app.component';
import { behaviorEventListeners, globalEventListeners } from '@client/app/core/run/setup.run';
import { uiRouterHooks } from '@client/app/core/run/basic.run';
import { DASHBOARD_STATES } from '@client/app/pages/dashboard/dashboard.route';
import { LOGIN_STATES } from '@client/app/pages/login/login.route';
import { UpgradeLoginModule } from '@client/app/pages/login/login.module';
import { DialogContainerModule } from '@monsido/core/modules/dialog-container/dialog-container.module';
import { ADMIN_STATES } from '@client/app/pages/admin/admin.route';
import { DOMAIN_STATES } from '@client/app/pages/domain/domain.route';
import { GLOBAL_STATES } from '@client/app/pages/global/global.route';
import { GLOBAL_POLICY_STATES } from '@client/app/pages/global/policy/global-policy.route';
import { HEATMAPS_STATES } from '@client/app/modules/heatmaps/pages/heatmaps.route';
import { POLICY_STATES } from '@client/app/pages/policy/policy.route';
import { QA_STATES } from '@client/app/pages/qa/qa.route';
import { QA_BROKEN_LINKS_STATES } from '@client/app/pages/qa/broken-links/broken.route';
import { QA_SPELL_CHECK_STATES } from '@client/app/pages/qa/spellcheck/spellcheck.route';
import { QA_READABILITY_STATES } from '@client/app/pages/qa/readability/readability.route';
import { QA_LANGUAGE_MATCH_STATES } from '@client/app/pages/qa/language-match/language-match.route';
import { ACCESSIBILITY_FUTURE_STATE } from '@client/app/pages/accessibility/accessibility-future.state';
import { UPTIMES_STATES } from '@client/app/pages/uptimes/uptimes.route';
import { PAGE_FIX_STATES } from '@client/app/modules/page-fix/pages/page-fix.route';
import { NavigationModule } from '@client/app/feature-modules/navigation/navigation.module';
import { DATA_PRIVACY_STATES } from '@client/app/modules/data-privacy/pages/pages.route';
import { PAGE_PERFORMANCE_STATES } from '@client/app/modules/page-performance/page-performance.route';
import { AppClockService } from '@client/app/core/services/app-clock.service';
import { ThemeService } from '@client/app/services/theme.service';

@NgModule({
    declarations: [
        DialogUpgradeWrapperComponent,
        AppComponent,
    ],
    exports: [
        DialogUpgradeWrapperComponent,
    ],
    imports: [
        TermsModule,
        TranslocoRootModule,
        PagePerformanceModule,
        ExportModule,
        TranslateModule,
        QuickHelpComplianceStatusModule,
        LoaderModule,
        LoadingModule,
        BrowserAnimationsModule,
        CommonModule,
        UpgradeFormsModule,
        MonEventModule,
        MonBiModule,
        TrackingModule,
        MonScoreComponentsModule,
        MonScoreServicesModule,
        MonBrowserModule,
        MonLegacyBrowserModule,
        MonCookieBannerModule,
        QaModule,
        AccessibilityComponentsModule,
        MonIssueModule,
        MonInventoryModule,
        PageDetailsModule,
        DataPrivacyModule,
        MonLabelsModule,
        OverlayWrapperModule,
        ReportCenterModule,
        StatisticsModule,
        ActiveFeatureModule,
        NgbModule,
        AdminModule,
        NavigationModule,
        CacheModule,
        ChatModule,
        SpotlightModule,
        MomentsModule,
        DeeplinkModule,
        ModalsModule,
        ExtensionModule,
        NotificationsModule,
        ChartsModule,
        UpgradePipeModule,
        ComponentsModule,
        UpgradeFormsModule,
        DomainFormModule,
        UpgradeDataPrivacyModule,
        UpgradeGlobalModule,
        UpgradePolicyModule,
        UpgradeDomainModule,
        UpgradeDashboardModule,
        UpgradeUptimesModule,
        UpgradeHeatmapsModule,
        UpgradeLayoutModule,
        UpgradeLoginModule,
        UIRouterModule,
        UIRouterModule.forRoot({
            states: [
                ...LAYOUT_STATES,
            ],
            config: (router: UIRouter) => {
                router.urlService.rules.initial({ state: 'base.dashboard' });
                router.urlService.rules.otherwise({ state: 'base.dashboard' });
            },
        }),
        UIRouterModule.forChild({
            states: [
                ...LOGIN_STATES,
                ...DOMAIN_STATES,
                ...DASHBOARD_STATES,
                ...ADMIN_STATES,
                ...GLOBAL_POLICY_STATES,
                ...GLOBAL_STATES,
                ...HEATMAPS_STATES,
                ...POLICY_STATES,
                ...QA_STATES,
                ...QA_BROKEN_LINKS_STATES,
                ...QA_SPELL_CHECK_STATES,
                ...QA_READABILITY_STATES,
                ...QA_LANGUAGE_MATCH_STATES,
                ACCESSIBILITY_FUTURE_STATE,
                ...UPTIMES_STATES,
                ...PAGE_FIX_STATES,
                ...DATA_PRIVACY_STATES,
                ...PAGE_PERFORMANCE_STATES,
                ...NotificationsStates,
            ],
        }),
        DialogContainerModule,
        PagesModule,
    ],
    providers: [
        RollbarErrorHandlerService,
        OverlayService,
        DirectNavigationService,
        { provide: ErrorHandler, useClass: RollbarErrorHandlerService },
        { provide: RollbarService, useFactory: rollbarFactory },
        CustomerHasPlanPipe,
        IsStatsDemoDataPipe,
        RolePipe,
        PipeService,
        provideAppInitializer(() => {
            globalEventListeners();
            behaviorEventListeners();
            uiRouterHooks();
            const appClockService = inject(AppClockService);
            appClockService.start();
            inject(ThemeService);
        }),
        {
            provide: NgbDateAdapter,
            // We pass a Date object into ngbDatePicker, we get a Date object out of it
            useClass: NgbDateNativeAdapter,
        },
        {
            provide: NgbTimeAdapter,
            useValue: {
                fromModel: (value: Date | null): NgbTimeStruct => {
                    if (!value) {
                        return {
                            hour: 0,
                            minute: 0,
                            second: 0,
                        };
                    }
                    return {
                        hour: value.getHours(),
                        minute: value.getMinutes(),
                        second: value.getSeconds(),
                    };
                },
                toModel: (time: NgbTimeStruct | null): Date => {
                    const results = new Date();
                    if (!time) {
                        results.setHours(0, 0, 0);
                        return results;
                    }
                    const { hour, minute, second } = time;
                    results.setHours(hour, minute, second);
                    return results;
                },
            },
        },
        providePrimeNG({
            theme: {
                preset: OptimizePreset,
                options: {
                    darkModeSelector: false,
                },
            },
            csp: {
                nonce: 'a1b2c3d4e5',
            },
        }),
        PrimeNGTranslationService,
        {
            provide: CSP_NONCE,
            useValue: 'a1b2c3d4e5',
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
